import React, { useState, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc, updateDoc } from 'firebase/firestore';
import { Box, Typography, CircularProgress, Button, Paper, Container } from '@mui/material';

const PaymentVerificationRoute = ({ children }) => {
  const auth = getAuth();
  const db = getFirestore();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [paymentVerified, setPaymentVerified] = useState(false);
  const [error, setError] = useState(null);
  const [user, setUser] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);

  // First, ensure we have a stable auth state
  useEffect(() => {
    console.log('PaymentVerificationRoute: Setting up auth state listener');

    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      console.log('PaymentVerificationRoute: Auth state changed',
        currentUser ? `User: ${currentUser.uid}` : 'No user');
      setUser(currentUser);
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, [auth]);

  // Check if payment is complete in localStorage immediately
  useEffect(() => {
    const localPaymentComplete = localStorage.getItem('paymentComplete');

    if (localPaymentComplete === 'true') {
      console.log('Payment verified from localStorage immediately');
      setPaymentVerified(true);
      setLoading(false);
      return;
    }

    // Check for our new payment data structure first
    const stripePaymentParams = localStorage.getItem('stripePaymentParams');
    let paymentParamsData = null;

    // Try to parse the JSON data safely
    if (stripePaymentParams) {
      try {
        paymentParamsData = JSON.parse(stripePaymentParams);
        console.log('Payment verified from new payment data structure:', paymentParamsData);
        setPaymentVerified(true);
        setLoading(false);
        return;
      } catch (error) {
        console.error('Error parsing payment params:', error);
        // Invalid JSON, clear it
        localStorage.removeItem('stripePaymentParams');
      }
    }

    // EMERGENCY FIX: Only as fallback if our new solution isn't used
    if (auth.currentUser) {
      console.log('EMERGENCY FIX: Setting payment status to true for authenticated user (as fallback)');
      localStorage.setItem('paymentComplete', 'true');
      setPaymentVerified(true);
      setLoading(false);
    }
  }, [auth]);

  // Then verify payment status once auth is checked
  useEffect(() => {
    const verifyPayment = async () => {
      // If auth hasn't been checked yet, wait
      if (!authChecked) return;

      // If no user, we're done loading
      if (!user) {
        setLoading(false);
        return;
      }

      // Check for our new payment data structure first
      const stripePaymentParams = localStorage.getItem('stripePaymentParams');
      let paymentParamsData = null;

      // Try to parse the JSON data safely
      if (stripePaymentParams) {
        try {
          paymentParamsData = JSON.parse(stripePaymentParams);
          console.log('Payment verified from new payment data structure in second useEffect:', paymentParamsData);
          setPaymentVerified(true);
          setLoading(false);
          return;
        } catch (error) {
          console.error('Error parsing payment params in second useEffect:', error);
          // Invalid JSON, clear it
          localStorage.removeItem('stripePaymentParams');
        }
      }

      // EMERGENCY FIX: Only as fallback if our new solution isn't used
      if (auth.currentUser) {
        console.log('EMERGENCY FIX: Setting payment status to true for authenticated user in second useEffect (as fallback)');
        localStorage.setItem('paymentComplete', 'true');
        setPaymentVerified(true);
        setLoading(false);
        return;
      }

      // If payment already verified from localStorage, we're done
      if (paymentVerified) {
        setLoading(false);
        return;
      }

      try {
        console.log('Verifying payment status for user:', user.uid);

        // Check localStorage again (just to be sure)
        const localPaymentComplete = localStorage.getItem('paymentComplete');
        const stripeSessionId = localStorage.getItem('stripeSessionId');

        console.log('Local storage payment status:', localPaymentComplete);
        console.log('Local storage session ID:', stripeSessionId);

        if (localPaymentComplete === 'true') {
          console.log('Payment verified from localStorage');
          setPaymentVerified(true);
          setLoading(false);
          return;
        }

        // If not in localStorage, check Firestore
        const userDoc = await getDoc(doc(db, 'users', user.uid));

        if (userDoc.exists()) {
          const userData = userDoc.data();
          console.log('User data:', userData);

          if (userData.paymentComplete) {
            console.log('Payment verified from Firestore for user:', user.uid);
            setPaymentVerified(true);
            // Update localStorage for future reference
            localStorage.setItem('paymentComplete', 'true');
          } else {
            console.log('Payment not verified for user:', user.uid);

            // As a last resort, check if the business document has payment info
            try {
              const businessDoc = await getDoc(doc(db, 'businesses', user.uid));
              if (businessDoc.exists() && businessDoc.data().paymentComplete) {
                console.log('Payment verified from business document');
                setPaymentVerified(true);
                // Update localStorage for future reference
                localStorage.setItem('paymentComplete', 'true');
                // Try to update the user document as well
                try {
                  await updateDoc(doc(db, 'users', user.uid), {
                    paymentComplete: true,
                    updatedAt: new Date()
                  });
                  console.log('Updated user document with payment status');
                } catch (updateErr) {
                  console.error('Error updating user document:', updateErr);
                }
                return;
              }
            } catch (businessErr) {
              console.error('Error checking business document:', businessErr);
            }

            // Final check - if we have a session ID in localStorage, assume payment is complete
            if (stripeSessionId) {
              console.log('Found Stripe session ID in localStorage, assuming payment is complete');
              setPaymentVerified(true);
              localStorage.setItem('paymentComplete', 'true');

              // Try to update both documents
              try {
                await updateDoc(doc(db, 'users', user.uid), {
                  paymentComplete: true,
                  stripeSessionId: stripeSessionId,
                  updatedAt: new Date()
                });
                console.log('Updated user document with payment status and session ID');

                await updateDoc(doc(db, 'businesses', user.uid), {
                  paymentComplete: true,
                  stripeSessionId: stripeSessionId,
                  updatedAt: new Date()
                });
                console.log('Updated business document with payment status and session ID');
              } catch (updateErr) {
                console.error('Error updating documents:', updateErr);
              }
              return;
            }

            setError('Payment required to access onboarding');
          }
        } else {
          console.log('User document not found');
          setError('User profile not found');
        }
      } catch (err) {
        console.error('Error verifying payment:', err);
        setError(`Error verifying payment: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    verifyPayment();
  }, [authChecked, user, db, paymentVerified]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (authChecked && !user) {
    // User is not authenticated, redirect to login
    console.log('No current user, redirecting to login from:', location.pathname);
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!paymentVerified) {
    // Payment not verified, show subscription page
    return (
      <Container maxWidth="md" sx={{ mt: 8 }}>
        <Paper elevation={3} sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="h4" gutterBottom>
            Payment Required
          </Typography>
          <Typography variant="body1" paragraph>
            You need to complete payment before accessing the onboarding process.
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {error}
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Button
              variant="contained"
              color="primary"
              href="/subscription"
              sx={{ mr: 2 }}
            >
              Subscribe Now
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => auth.signOut()}
            >
              Sign Out
            </Button>
          </Box>
        </Paper>
      </Container>
    );
  }

  // User is authenticated and payment is verified, render the protected component
  console.log('User is authenticated and payment verified, rendering children for path:', location.pathname);
  return <>{children}</>;
};

export default PaymentVerificationRoute;
